import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import {
    compactShippingProgress,
    describeShippingProgress,
    listShippingProgress,
} from '../../utils/shippingBehavior';

const BANNER_ID = 'home-shipping-banner';

const headerCover = () => {
    const header = document.querySelector('header');
    if (!header) return 0;
    const position = window.getComputedStyle(header).position;
    if (position !== 'fixed' && position !== 'sticky') return 0;
    return Math.max(0, header.getBoundingClientRect().bottom);
};

const bannerHasCleared = () => {
    const element = document.getElementById(BANNER_ID);
    if (!element) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;

    return rect.bottom <= headerCover() + 0.5;
};

const ShippingProgressChip = () => {
    const location = useLocation();
    const activeOrder = useSelector((state) => state.cart?.activeOrder);
    const companiesByRuc = useSelector((state) => state.companies?.companiesByRuc || {});
    const [scrolledPast, setScrolledPast] = useState(false);
    const [justReached, setJustReached] = useState(false);
    const scrolledPastRef = useRef(false);
    const celebratedRef = useRef(false);

    const entries = listShippingProgress({ activeOrder, companiesByRuc });
    const summary = compactShippingProgress(entries);
    const summaryKey = summary ? `${summary.label}|${summary.progress}` : '';

    useEffect(() => {
        if (!summaryKey) {
            scrolledPastRef.current = false;
            setScrolledPast(false);
            return undefined;
        }

        let frame = 0;
        let active = true;

        const apply = () => {
            frame = 0;
            if (!active) return;
            const next = bannerHasCleared();
            if (next === scrolledPastRef.current) return;
            scrolledPastRef.current = next;
            setScrolledPast(next);
        };

        const schedule = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(apply);
        };

        apply();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        window.visualViewport?.addEventListener('scroll', schedule);
        window.visualViewport?.addEventListener('resize', schedule);

        return () => {
            active = false;
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            window.visualViewport?.removeEventListener('scroll', schedule);
            window.visualViewport?.removeEventListener('resize', schedule);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [summaryKey, location.pathname]);

    useEffect(() => {
        if (!summary?.reached) {
            celebratedRef.current = false;
            setJustReached(false);
            return undefined;
        }
        if (!scrolledPast) {
            setJustReached(false);
            return undefined;
        }
        if (celebratedRef.current) return undefined;

        celebratedRef.current = true;
        setJustReached(true);
        return undefined;
    }, [summary?.reached, scrolledPast]);

    if (!summary || !scrolledPast) {
        return null;
    }

    const detail = describeShippingProgress({
        ...summary.entry,
        companyName: summary.entry?.name,
        includeCompanyName: entries.length > 1,
    });
    const progress = Math.max(0, Math.min(100, Math.round(Number(summary.progress) || 0)));

    return (
        <Link
            to="/cart"
            className={`shipping-progress-chip${summary.reached ? ' is-reached' : ''}${justReached ? ' is-celebrate' : ''}`}
            aria-label={detail.message}
            title={detail.message}
            onAnimationEnd={() => setJustReached(false)}
        >
            <svg className="shipping-progress-ring" viewBox="0 0 36 36" aria-hidden="true">
                <circle className="shipping-progress-ring-track" cx="18" cy="18" r="15.915" />
                <circle
                    className="shipping-progress-ring-value"
                    cx="18"
                    cy="18"
                    r="15.915"
                    strokeDasharray={`${progress} 100`}
                    strokeLinecap={progress >= 100 ? 'butt' : 'round'}
                />
            </svg>
            <span className="shipping-progress-chip-center">
                <span className="shipping-progress-chip-emoji" aria-hidden="true">🚚</span>
                <span className="shipping-progress-chip-phrase">
                    <span>Envío</span>
                    <span>gratis!</span>
                </span>
            </span>
            <span
                className="visually-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                aria-label="Avance hacia el envío gratis"
            />
        </Link>
    );
};

export default ShippingProgressChip;
